import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatNumber(num) {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('nb-NO');
}

function formatDuration(ms) {
    if (!ms || ms <= 0) return '0s';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${seconds}s`;
    return `${minutes}m ${seconds}s`;
}

function formatShortDate(dateStr) {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}.${parts[1]}`;
}

@observer
class ConventionStatsDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            stats: null,
            isLoading: true,
            error: null,
            period: 'all', // 'today', '7d', '30d', 'all', 'custom'
            customStartDate: '',
            customEndDate: '',
        };
    }

    loadStats = async (periodOverride) => {
        const { conventionId } = this.props;
        const { period, customStartDate, customEndDate } = this.state;
        const activePeriod = periodOverride || period;

        if (!conventionId) {
            this.setState({ error: 'No convention ID provided', isLoading: false });
            return;
        }

        this.setState({ isLoading: true });

        try {
            let url = `/api/conventions/${conventionId}/stats?period=${activePeriod}`;
            if (activePeriod === 'custom' && customStartDate && customEndDate) {
                url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
            }
            const response = await util.fetchApi(url, { method: 'GET' });
            if (response.status === 200) {
                this.setState({ stats: response.data, isLoading: false });
            } else {
                this.setState({ error: response.message || 'Failed to load stats', isLoading: false });
            }
        } catch (err) {
            this.setState({ error: err.message, isLoading: false });
        }
    }

    handlePeriodChange = (newPeriod) => {
        this.setState({ period: newPeriod }, () => {
            if (newPeriod !== 'custom') {
                this.loadStats(newPeriod);
            }
        });
    }

    handleDateChange = (field, value) => {
        this.setState({ [field]: value });
    }

    handleCustomDateSubmit = () => {
        const { customStartDate, customEndDate } = this.state;
        if (customStartDate && customEndDate) {
            this.loadStats('custom');
        }
    }

    componentDidMount() {
        this.loadStats();
    }

    renderTrend(trend) {
        if (!trend || trend === 0) return null;
        const isPositive = trend > 0;
        return (
            <span class={`ms-1 ${isPositive ? 'text-success' : 'text-danger'}`}>
                {isPositive ? '↑' : '↓'}{Math.abs(trend)}%
            </span>
        );
    }

    renderSummaryCard(title, value, icon, trend, darkmode, subtitle) {
        return (
            <div class='col-6 col-md-3 mb-3'>
                <div class={`rounded-lg p-3 h-100 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='text-muted small mb-1'>
                        <i class={`fa-solid ${icon} me-1`} />{title}
                    </div>
                    <div class='d-flex align-items-baseline'>
                        <span class='h4 mb-0 font-weight-bold'>{value}</span>
                        {this.renderTrend(trend)}
                    </div>
                    {subtitle && <div class='text-muted small mt-1'>{subtitle}</div>}
                </div>
            </div>
        );
    }

    renderEventCountdown(convention) {
        if (!convention?.startDate) return null;
        const start = new Date(convention.startDate);
        const now = new Date();
        const days = Math.ceil((start.getTime() - now.getTime()) / 86400000);
        let label;
        if (days > 1) {
            label = `Event starts in ${days} days`;
        } else if (days === 1) {
            label = 'Event starts tomorrow';
        } else if (days === 0) {
            label = 'Event day is today!';
        } else if (days === -1) {
            label = 'Event was yesterday';
        } else {
            label = `Event was ${Math.abs(days)} days ago`;
        }
        return (
            <div class='text-muted small'>
                <i class='fa-solid fa-calendar-day me-1' />
                {start.toLocaleDateString('nb-NO')} — {label}
            </div>
        );
    }

    renderFunnel(funnel, summary) {
        const views = funnel?.views || 0;
        const loggedIn = funnel?.loggedInUsers || 0;
        const signups = funnel?.signups || 0;

        if (views === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No views in this period yet
                </div>
            );
        }

        const uniqueVisitors = funnel?.uniqueVisitors || 0;

        // Unique visitors (distinct IPs) only exist once scrollView sessions have been
        // collected; fall back to logged-in visitors for older data.
        const reachStep = uniqueVisitors > 0
            ? { label: 'Unique visitors', value: uniqueVisitors, color: 'bg-info', icon: 'fa-users' }
            : { label: 'Logged-in visitors', value: loggedIn, color: 'bg-info', icon: 'fa-user-check' };

        const steps = [
            { label: 'Page views', value: views, color: 'bg-primary', icon: 'fa-eye' },
            reachStep,
            { label: 'Signups', value: signups, color: 'bg-success', icon: 'fa-user-plus' },
        ];

        return (
            <div>
                {steps.map(step => {
                    const percent = views > 0 ? Math.round((step.value / views) * 100) : 0;
                    const barWidth = Math.max(percent, step.value > 0 ? 2 : 0);
                    return (
                        <div class='mb-2' key={step.label}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span><i class={`fa-solid ${step.icon} me-1`} />{step.label}</span>
                                <span class='text-muted'>{formatNumber(step.value)} ({percent}%)</span>
                            </div>
                            <div class='progress' style='height: 14px;'>
                                <div
                                    class={`progress-bar ${step.color}`}
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
                <div class='text-muted small mt-2'>
                    <i class='fa-solid fa-arrow-trend-up me-1' />
                    {formatNumber(signups)} of {formatNumber(views)} views converted to a signup ({summary?.conversionRate || 0}%)
                </div>
            </div>
        );
    }

    getCountryFlag(countryCode) {
        if (!countryCode || countryCode.length !== 2) return '';
        // Convert country code to flag emoji (regional indicator symbols)
        const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    }

    renderVisitorMap(cityBreakdown) {
        const cities = (cityBreakdown || []).filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lon));
        if (cities.length === 0) {
            return null;
        }
        const maxVisitors = Math.max(...cities.map(c => c.visitors));
        const bounds = cities.map(c => [c.lat, c.lon]);

        return (
            <div class='rounded-lg overflow-hidden' style='height: 300px;'>
                <MapContainer
                    bounds={bounds}
                    boundsOptions={{ padding: [40, 40], maxZoom: 10 }}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    />
                    {cities.map(c => (
                        <CircleMarker
                            key={`${c.city}-${c.countryCode}`}
                            center={[c.lat, c.lon]}
                            radius={6 + Math.round(14 * Math.sqrt(c.visitors / maxVisitors))}
                            pathOptions={{ color: '#0d6efd', fillColor: '#0d6efd', fillOpacity: 0.4 }}
                        >
                            <Popup>
                                <strong>{c.city}</strong>{c.country ? `, ${c.country}` : ''}<br />
                                {formatNumber(c.visitors)} {c.visitors === 1 ? 'visitor' : 'visitors'}
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>
        );
    }

    renderCountryBreakdown(countryBreakdown) {
        if (!countryBreakdown || countryBreakdown.length === 0) {
            return null;
        }

        const maxVisitors = Math.max(...countryBreakdown.map(c => c.visitors));

        return (
            <div>
                {countryBreakdown.map((country, idx) => {
                    const barWidth = maxVisitors > 0 ? (country.visitors / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={country.countryCode || idx}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {country.countryCode && <span class='me-2'>{this.getCountryFlag(country.countryCode)}</span>}
                                    {country.country}
                                </span>
                                <span class='text-muted'>{formatNumber(country.visitors)} ({country.percentage}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class='progress-bar bg-primary'
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderCityBreakdown(cityBreakdown) {
        if (!cityBreakdown || cityBreakdown.length === 0) {
            return null;
        }

        const maxVisitors = Math.max(...cityBreakdown.map(c => c.visitors));

        return (
            <div>
                {cityBreakdown.map((city, idx) => {
                    const barWidth = maxVisitors > 0 ? (city.visitors / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={`${city.city}-${city.countryCode}` || idx}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {city.countryCode && <span class='me-2'>{this.getCountryFlag(city.countryCode)}</span>}
                                    {city.city}{city.country ? ` (${city.country})` : ''}
                                </span>
                                <span class='text-muted'>{formatNumber(city.visitors)} ({city.percentage}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div
                                    class='progress-bar bg-info'
                                    style={`width: ${barWidth}%;`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderDailyChart(dailyStats, eventDateStr) {
        if (!dailyStats || dailyStats.length === 0) {
            return (
                <div class='text-muted text-center py-4'>
                    No daily data available yet
                </div>
            );
        }

        const maxTotal = Math.max(...dailyStats.map(d => (d.views || 0) + (d.signups || 0) + (d.resultViews || 0) + (d.resultsEntered || 0)));
        const displayData = dailyStats.slice(-30);
        const barWidth = Math.floor(100 / Math.min(displayData.length, 30));

        return (
            <div class='d-flex align-items-end justify-content-between' style='height: 130px; overflow-x: auto;'>
                {displayData.map((day, idx) => {
                    const total = (day.views || 0) + (day.signups || 0) + (day.resultViews || 0) + (day.resultsEntered || 0);
                    const viewsHeight = maxTotal > 0 ? ((day.views || 0) / maxTotal) * 100 : 0;
                    const signupsHeight = maxTotal > 0 ? ((day.signups || 0) / maxTotal) * 100 : 0;
                    const resultViewsHeight = maxTotal > 0 ? ((day.resultViews || 0) / maxTotal) * 100 : 0;
                    const resultsEnteredHeight = maxTotal > 0 ? ((day.resultsEntered || 0) / maxTotal) * 100 : 0;
                    const isEventDay = eventDateStr && day.date === eventDateStr;
                    const dateLabel = day.date ? day.date.split('-')[2] : idx + 1;

                    return (
                        <div
                            key={idx}
                            class='d-flex flex-column align-items-center flex-shrink-0'
                            style={`width: ${barWidth}%; min-width: 12px; max-width: 24px;`}
                            title={`${day.date}${isEventDay ? ' (event day)' : ''}: ${formatNumber(total)} total`}
                        >
                            <div class='d-flex flex-column-reverse' style='height: 100px; width: 100%;'>
                                <div class='bg-primary' style={`height: ${viewsHeight}%; min-height: ${day.views > 0 ? '2px' : '0'};`} />
                                <div class='bg-success' style={`height: ${signupsHeight}%; min-height: ${day.signups > 0 ? '2px' : '0'};`} />
                                <div class='bg-info' style={`height: ${resultViewsHeight}%; min-height: ${day.resultViews > 0 ? '2px' : '0'};`} />
                                <div class='bg-warning' style={`height: ${resultsEnteredHeight}%; min-height: ${day.resultsEntered > 0 ? '2px' : '0'};`} />
                            </div>
                            <div
                                class={`small mt-1 ${isEventDay ? 'text-warning fw-bold' : 'text-muted'}`}
                                style='font-size: 0.55rem;'
                            >
                                {isEventDay ? <i class='fa-solid fa-star' /> : dateLabel}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderPeakHours(peakHours) {
        if (!peakHours || peakHours.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No peak hours data available
                </div>
            );
        }

        const maxViews = Math.max(...peakHours.map(p => p.views || 0));
        const chartHeight = 80;

        return (
            <div>
                <div class='d-flex align-items-end justify-content-between' style={`height: ${chartHeight}px;`}>
                    {peakHours.map((hour, idx) => {
                        const barHeight = maxViews > 0
                            ? Math.max(((hour.views || 0) / maxViews) * chartHeight, 2)
                            : 2;
                        const isPeak = hour.views === maxViews && maxViews > 0;

                        return (
                            <div
                                key={idx}
                                class='d-flex flex-column justify-content-end'
                                style={`width: 4%; min-width: 8px; height: ${chartHeight}px;`}
                                title={`${hour.hour}:00 - ${formatNumber(hour.views)} views`}
                            >
                                <div
                                    class={isPeak ? 'bg-warning' : 'bg-primary'}
                                    style={`height: ${barHeight}px; width: 100%; border-radius: 2px 2px 0 0;`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-between text-muted small mt-1'>
                    <span>00:00</span>
                    <span>06:00</span>
                    <span>12:00</span>
                    <span>18:00</span>
                    <span>23:00</span>
                </div>
            </div>
        );
    }

    renderWeekdays(weekdayStats) {
        if (!weekdayStats || weekdayStats.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No weekday data available
                </div>
            );
        }

        const maxViews = Math.max(...weekdayStats.map(w => w.views || 0));
        const chartHeight = 80;
        const best = weekdayStats.reduce((max, w) => (w.views > max.views ? w : max), weekdayStats[0]);

        return (
            <div>
                <div class='d-flex align-items-end justify-content-around' style={`height: ${chartHeight}px;`}>
                    {weekdayStats.map(day => {
                        const barHeight = maxViews > 0
                            ? Math.max(((day.views || 0) / maxViews) * chartHeight, 2)
                            : 2;
                        const isBest = maxViews > 0 && day.views === maxViews;

                        return (
                            <div
                                key={day.isoWeekday}
                                class='d-flex flex-column justify-content-end align-items-center'
                                style={`width: 10%; height: ${chartHeight}px;`}
                                title={`${WEEKDAY_LABELS[day.isoWeekday - 1]}: ${formatNumber(day.views)} views, ${formatNumber(day.signups)} signups`}
                            >
                                <div
                                    class={isBest ? 'bg-warning' : 'bg-primary'}
                                    style={`height: ${barHeight}px; width: 100%; max-width: 40px; border-radius: 2px 2px 0 0;`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-around text-muted small mt-1'>
                    {weekdayStats.map(day => (
                        <span key={day.isoWeekday} style='width: 10%; text-align: center;'>
                            {WEEKDAY_LABELS[day.isoWeekday - 1]}
                        </span>
                    ))}
                </div>
                {maxViews > 0 && (
                    <div class='text-muted small mt-2'>
                        <i class='fa-solid fa-lightbulb me-1' />
                        Most traffic on {WEEKDAY_LABELS[best.isoWeekday - 1]} — a good day to post announcements.
                    </div>
                )}
            </div>
        );
    }

    renderWeeklyHistory(weeklyStats, darkmode) {
        if (!weeklyStats || weeklyStats.length < 2) {
            return null;
        }

        const displayData = weeklyStats.slice(-12);
        const maxTotal = Math.max(...displayData.map(w => (w.views || 0) + (w.signups || 0)));
        const chartHeight = 80;

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <h6 class='mb-3'>
                    <i class='fa-solid fa-calendar-week me-2' />
                    Weekly History (Last 12 Weeks)
                </h6>
                <div class='d-flex align-items-end justify-content-around' style={`height: ${chartHeight}px;`}>
                    {displayData.map(week => {
                        const viewsHeight = maxTotal > 0 ? ((week.views || 0) / maxTotal) * chartHeight : 0;
                        const signupsHeight = maxTotal > 0 ? ((week.signups || 0) / maxTotal) * chartHeight : 0;
                        return (
                            <div
                                key={week.week}
                                class='d-flex flex-column justify-content-end align-items-center'
                                style={`width: 7%; height: ${chartHeight}px;`}
                                title={`Week ${week.week}: ${formatNumber(week.views)} views, ${formatNumber(week.signups)} signups`}
                            >
                                <div class='d-flex flex-column-reverse' style='width: 100%; max-width: 28px;'>
                                    <div class='bg-primary' style={`height: ${Math.max(viewsHeight, week.views > 0 ? 2 : 0)}px; border-radius: 2px 2px 0 0;`} />
                                    <div class='bg-success' style={`height: ${Math.max(signupsHeight, week.signups > 0 ? 2 : 0)}px;`} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-around text-muted small mt-1'>
                    {displayData.map(week => (
                        <span key={week.week} style='width: 7%; text-align: center; font-size: 0.6rem;'>
                            {String(week.week).replace(/^\d{4}-?W?/, '')}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    renderPeriodSelector(darkmode) {
        const { period, customStartDate, customEndDate, isLoading } = this.state;

        const periods = [
            { key: 'today', label: 'Today' },
            { key: '7d', label: '7 days' },
            { key: '30d', label: '30 days' },
            { key: 'all', label: 'All time' },
            { key: 'custom', label: 'Custom' },
        ];

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex flex-wrap align-items-center gap-2'>
                    <span class='text-muted small me-2'>
                        <i class='fa-solid fa-filter me-1' />
                        Period:
                    </span>
                    <div class='btn-group btn-group-sm'>
                        {periods.map(p => (
                            <button
                                key={p.key}
                                type='button'
                                class={`btn ${period === p.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => this.handlePeriodChange(p.key)}
                                disabled={isLoading}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {period === 'custom' && (
                        <div class='d-flex align-items-center gap-2 ms-2'>
                            <input
                                type='date'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customStartDate}
                                onChange={(e) => this.handleDateChange('customStartDate', e.target.value)}
                            />
                            <span class='text-muted'>to</span>
                            <input
                                type='date'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customEndDate}
                                onChange={(e) => this.handleDateChange('customEndDate', e.target.value)}
                            />
                            <button
                                type='button'
                                class='btn btn-sm btn-primary'
                                onClick={this.handleCustomDateSubmit}
                                disabled={!customStartDate || !customEndDate || isLoading}
                            >
                                <i class='fa-solid fa-search' />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { stats, isLoading, error, period } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (isLoading && !stats) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center' style='min-height: 200px;'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-danger'>{error}</div>
                </div>
            );
        }

        if (!stats) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-warning'>No statistics available</div>
                </div>
            );
        }

        const {
            convention, summary, funnel, dailyStats, peakHours, weekdayStats, weeklyStats,
            deviceBreakdown, countryBreakdown, cityBreakdown,
        } = stats;
        const hasSessions = summary?.totalSessions > 0;
        const hasGeoData = countryBreakdown?.length > 0 || cityBreakdown?.length > 0;
        const visitorMap = this.renderVisitorMap(cityBreakdown);

        // Event day as YYYY-MM-DD for highlighting in the daily chart.
        let eventDateStr = '';
        if (convention?.startDate) {
            const d = new Date(convention.startDate);
            eventDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding: 20px; padding-bottom: 100px;'>

                    <div class='mb-4'>
                        <h5 class='mb-1'>
                            <i class='fa-solid fa-chart-line me-2' />
                            Statistics: {convention?.title}
                            {isLoading && <span class='spinner-border spinner-border-sm ms-2' />}
                        </h5>
                        {this.renderEventCountdown(convention)}
                    </div>

                    {/* Period Selector */}
                    {this.renderPeriodSelector(darkmode)}

                    {/* Summary Cards */}
                    <div class='row'>
                        {this.renderSummaryCard('Total Views', formatNumber(summary?.totalViews), 'fa-eye', period === 'all' ? summary?.viewsTrend : null, darkmode)}
                        {this.renderSummaryCard('Signups', formatNumber(summary?.totalSignups), 'fa-user-plus', null, darkmode,
                            summary?.conversionRate > 0 ? `${summary.conversionRate}% of views` : null)}
                        {this.renderSummaryCard('Result Views', formatNumber(summary?.totalResultViews), 'fa-list', null, darkmode)}
                        {this.renderSummaryCard('Results Entered', formatNumber(summary?.totalResultsEntered), 'fa-trophy', null, darkmode)}
                    </div>
                    <div class='row'>
                        {this.renderSummaryCard('Unique Visitors', formatNumber(summary?.uniqueVisitors), 'fa-users', null, darkmode)}
                        {this.renderSummaryCard('Logged-in Users', formatNumber(summary?.loggedInUsers), 'fa-user-check', null, darkmode)}
                        {this.renderSummaryCard('Avg Session', hasSessions ? formatDuration(summary?.avgSessionDuration) : '—', 'fa-stopwatch', null, darkmode,
                            hasSessions ? `${formatNumber(summary.totalSessions)} sessions` : null)}
                        {this.renderSummaryCard('Mobile', hasSessions ? `${deviceBreakdown?.mobile || 0}%` : '—', 'fa-mobile-screen', null, darkmode)}
                    </div>
                    <div class='row'>
                        {this.renderSummaryCard('Views/User', summary?.viewsPerUser || '0', 'fa-chart-line', null, darkmode)}
                        {this.renderSummaryCard('Peak Hour', `${summary?.peakHour || 0}:00`, 'fa-clock', null, darkmode)}
                        {this.renderSummaryCard('Best Day', summary?.bestDay ? formatShortDate(summary.bestDay.date) : '—', 'fa-star', null, darkmode,
                            summary?.bestDay ? `${formatNumber(summary.bestDay.views)} views` : null)}
                    </div>

                    {/* Signup Funnel */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-filter-circle-dollar me-2' />
                            Signup Funnel
                        </h6>
                        {this.renderFunnel(funnel, summary)}
                    </div>

                    {/* Visitors Map */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-map-location-dot me-2' />
                            Visitors Map
                        </h6>
                        {visitorMap || (
                            <div class='text-muted text-center py-4'>
                                <i class='fa-solid fa-map-location-dot d-block mb-2' style='font-size: 1.5rem;' />
                                No visitor locations yet.<br />
                                <span class='small'>
                                    Locations are collected from visitor sessions from now on — the map fills in as people visit.
                                    Admin visits are not counted.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Country Breakdown */}
                    {hasGeoData && countryBreakdown?.length > 0 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-globe me-2' />
                                Visitors by Country
                            </h6>
                            {this.renderCountryBreakdown(countryBreakdown)}
                        </div>
                    )}

                    {/* City Breakdown */}
                    {hasGeoData && cityBreakdown?.length > 0 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-city me-2' />
                                Visitors by City
                            </h6>
                            {this.renderCityBreakdown(cityBreakdown)}
                        </div>
                    )}

                    {/* Daily Traffic Chart */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <div class='d-flex justify-content-between align-items-center mb-3 flex-wrap'>
                            <h6 class='mb-0'>
                                <i class='fa-solid fa-chart-bar me-2' />
                                Daily Traffic (Last 30 Days)
                            </h6>
                            <div class='d-flex gap-3 small'>
                                <span><span class='badge bg-primary'>&nbsp;</span> Views</span>
                                <span><span class='badge bg-success'>&nbsp;</span> Signups</span>
                                <span><span class='badge bg-info'>&nbsp;</span> Result views</span>
                                <span><span class='badge bg-warning'>&nbsp;</span> Results entered</span>
                            </div>
                        </div>
                        {this.renderDailyChart(dailyStats, eventDateStr)}
                    </div>

                    {/* Peak Hours Chart */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-clock me-2' />
                            Peak Hours (Views by Hour)
                        </h6>
                        {this.renderPeakHours(peakHours)}
                    </div>

                    {/* Best Weekday */}
                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-calendar-days me-2' />
                            Traffic by Weekday
                        </h6>
                        {this.renderWeekdays(weekdayStats)}
                    </div>

                    {/* Weekly History */}
                    {this.renderWeeklyHistory(weeklyStats, darkmode)}

                </div>
            </div>
        );
    }
}

export default ConventionStatsDrawer;
